'use client';

import { pillLabel } from '@zealot/ui/lib/filtering/clauseBuilders';
import type { FilterConfig } from '@zealot/ui/lib/filtering/filterConfig';
import { cn } from '@zealot/ui/lib/utils';
import { Filter, SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { Badge } from '../badge';
import { Button } from '../button';
import { IndicatorDot } from '../indicator-dot';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { emptyValueFor, FilterMenu, hasActiveValue } from './filter-menu';
import { FilterPill } from './filter-pill';

// gloss
const buildPillLabel = (config: FilterConfig, value: unknown): string => {
  switch (config.kind) {
    case 'multiSelect':
      return pillLabel(config, value as string[]);
    case 'dateRange':
      return pillLabel(config, value as { from: string | null; to: string | null });
    case 'text':
      return pillLabel(config, value as string);
    case 'boolean':
      return pillLabel(config, value as boolean);
  }
};

// gloss
type FilterPopoverProps = {
  configs: FilterConfig[];
  values: Record<string, unknown>;
  onValueChange: (key: string, value: unknown) => void;
  onClear?: () => void;
  className?: string;
  triggerLabel?: string;
  iconOnly?: boolean;
  defaultValues?: Record<string, unknown>;
  header?: React.ReactNode;
  extraActiveCount?: number;
  disabled?: boolean;
};

// gloss
export const activeToolbarTriggerClassName =
  'border-primary/20 bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary';

// gloss
const activeTriggerClassName = `${activeToolbarTriggerClassName} aria-expanded:bg-primary/15 aria-expanded:text-primary`;

// gloss
const isSameFilterValue = (config: FilterConfig, value: unknown, fallback: unknown): boolean => {
  if (!hasActiveValue(config, value) && !hasActiveValue(config, fallback)) return true;
  if (Array.isArray(value) && Array.isArray(fallback)) {
    return value.length === fallback.length && value.every((item) => fallback.includes(item));
  }
  if (config.kind === 'dateRange') {
    const range = value as { from?: string | null; to?: string | null } | undefined;
    const fallbackRange = fallback as typeof range;
    return (
      (range?.from ?? null) === (fallbackRange?.from ?? null) && (range?.to ?? null) === (fallbackRange?.to ?? null)
    );
  }
  return value === fallback;
};

// gloss
const FilterPopover = ({
  configs,
  values,
  onValueChange,
  onClear,
  className,
  triggerLabel = 'Filters',
  iconOnly,
  defaultValues = {},
  header,
  extraActiveCount = 0,
  disabled = false,
}: FilterPopoverProps) => {
  const [open, setOpen] = React.useState(false);
  const activeConfigs = configs.filter((config) => hasActiveValue(config, values[config.key]));
  const changedCount =
    configs.filter(
      (config) =>
        (hasActiveValue(config, values[config.key]) || hasActiveValue(config, defaultValues[config.key])) &&
        !isSameFilterValue(config, values[config.key], defaultValues[config.key]),
    ).length + extraActiveCount;
  const isActive = changedCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size={iconOnly ? 'icon-sm' : 'sm'}
            aria-label={iconOnly ? `Filter${isActive ? `, ${changedCount} active` : ''}` : undefined}
            className={cn(iconOnly && 'relative rounded-full', isActive && activeTriggerClassName, className)}
            disabled={disabled}
          />
        }
      >
        {iconOnly ? <Filter /> : <SlidersHorizontal data-icon="inline-start" />}
        {!iconOnly && triggerLabel}
        {isActive && (iconOnly ? <IndicatorDot /> : <Badge variant="default">{changedCount}</Badge>)}
      </PopoverTrigger>
      {}
      <PopoverContent align="end" motion="fade" className="w-[30rem] p-0">
        {header && <div className="border-border border-b px-3 py-2">{header}</div>}
        {activeConfigs.length > 0 && (
          <div className="flex max-h-24 flex-wrap items-center gap-1.5 overflow-y-auto border-border border-b px-3 py-2">
            {activeConfigs.map((config) => (
              <FilterPill
                key={config.key}
                label={buildPillLabel(config, values[config.key])}
                onRemove={() => onValueChange(config.key, emptyValueFor(config))}
              />
            ))}
            {onClear && (
              <button
                type="button"
                className={cn('px-2 py-1 text-muted-foreground text-xs hover:text-foreground')}
                onClick={onClear}
              >
                Clear all
              </button>
            )}
          </div>
        )}
        <FilterMenu configs={configs} values={values} onValueChange={onValueChange} />
      </PopoverContent>
    </Popover>
  );
};

export type { FilterPopoverProps };
export { FilterPopover };
